import  { createApp, watch } from "vue";
import App from "./App.vue";
import router from './router';
import { installAnalytics, identify } from './utils/analytics';
import { installProduct, identifyProduct } from './utils/product';
import MazPhoneNumberInput from 'maz-ui/components/MazPhoneNumberInput';
import { VueClipboard } from '@soerenmartius/vue3-clipboard';
import VueGoogleMaps from '@fawmi/vue-google-maps';
import { createPinia } from 'pinia';
import * as Sentry from "@sentry/vue";
import VueEasyLightbox from 'vue-easy-lightbox';



import 'maz-ui/css/main.css';
// import "~/styles/element/index.scss";

// import ElementPlus from "element-plus";
// import all element css, uncommented next line
// import "element-plus/dist/index.css";

// or use cdn, uncomment cdn link in `index.html`

import "~/styles/index.scss";
import "uno.css";

// If you want to use ElMessage, import it.
import "element-plus/theme-chalk/src/message.scss";
// import 'vue-easy-lightbox/dist/external-css/vue-easy-lightbox.css'



const pinia = createPinia()
const app = createApp(App);
app.component('MazPhoneNumberInput', MazPhoneNumberInput);
app.use(VueClipboard);
app.use(router);
// Usage analytics. A no-op unless a measurement id is baked in AND this is a
// SafiriOS host — Riley's own customers are never reported. See utils/analytics.
installAnalytics(router);
// PostHog, on our own box: the real url and the real person.
installProduct(router);
app.use(pinia);
// Tell Analytics who this is. A watcher rather than a call in setSession(),
// because the user also arrives from a localStorage restore and from the
// /session refresh — a watcher cannot miss a path, three call sites can.
// id, role and customer_id only: the same object holds a name and an email,
// which are exactly what Google's terms exclude and what we do not need.
// useAuthStore is imported further down this file; ES module imports are
// hoisted, so the binding is already live here.
watch(
    () => useAuthStore().user,
    (u) => {
        identify(u ? { userId: u.id, role: u.role, accountId: u.customer_id } : {});
        identifyProduct(u ? { id: u.id, email: u.email, name: u.name, role: u.role, accountId: u.customer_id } : null);
    },
    { immediate: true },
);
app.use(VueGoogleMaps, {
    load: {
        key: import.meta.env.VITE_GOOGLE_API_KEY,
        libraries: 'places,geometry',
        loading: 'async',
    },
})
app.use(VueEasyLightbox);

Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN || '',
    integrations: [
      new Sentry.BrowserTracing({
        tracePropagationTargets: [
          new RegExp('^' + (import.meta.env.VITE_API_BASE || 'http://localhost:8081').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
        ],
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
    ],
    tracesSampleRate: 0.2,
    // Sessions let us see the crash-free-session rate, not just a raw event count:
    // one blank page for one customer is a lost session, and that is the number
    // that tells us whether the portal is healthy.
    autoSessionTracking: true,
    // Set from a build-time env var when the deploy provides one. The DigitalOcean
    // static site `prod-riley-customer` does NOT define VITE_SENTRY_RELEASE today,
    // so this is undefined in production until that env var is added to the app
    // spec — reading it here costs nothing and makes it work the moment it is.
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
  });

// The mount watchdog in index.html is a classic (non-module) script, so it cannot
// import Sentry. Hand it the initialised client through window so that a failure
// to mount is still reportable.
(window as any).Sentry = Sentry;

// app.use(ElementPlus);

// Restore user object for instant UI, then validate cookie with the API
import { useAuthStore } from './store/authStore';
const store = useAuthStore();
store.restoreSession(); // instant UI from cached user; the router guard awaits
                        // store.init() to validate the cookie before the first route.

// Pull the settings-configured company name once on init (also sets document.title).
// The live-chat (Tawk) widget is loaded here, and only when TMS Settings has it
// enabled — OFF by default, so it never loads for tenants that don't want it.
import { useConfigStore } from './store/configStore';
function loadTawk() {
  if ((window as any).__tawkLoaded) return;
  (window as any).__tawkLoaded = true;
  (window as any).Tawk_API = (window as any).Tawk_API || {};
  (window as any).Tawk_LoadStart = new Date();
  const s1 = document.createElement('script');
  s1.async = true;
  s1.src = 'https://embed.tawk.to/69af14e83512f31c34332a06/1jj9ujbcb';
  s1.charset = 'UTF-8';
  s1.setAttribute('crossorigin', '*');
  document.body.appendChild(s1);
}
useConfigStore().fetchConfig().then(() => {
  if (useConfigStore().chatEnabled) loadTawk();
});

app.mount("#app");
// Read by the mount watchdog in index.html: set the instant the app is on screen
// so the watchdog can cancel itself without touching the DOM.
(window as any).__portalMounted = true;

// Poll /customer/profile on tab focus to detect expired sessions
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    store.pollProfile();
  }
});
